<template>
  <div class="container">
    <ProgressHeader step="ssn" />

    <!-- Make search work for initial load -->
    <ComboBox
      v-model="selectedCountries"
      label="Citizenship countries"
      ref="citizenshipCountries"
      multiple
      class="pt-4"
      compareValue="id"
      @search="updateQueryString"
    >
      <ComboBoxOption
        v-for="country in filteredCountries"
        :key="country.id"
        :value="country"
        :text="country.name"
        @click="updateQueryString('')"
      >
      </ComboBoxOption>
    </ComboBox>

    <p class="mt-2 text-sm text-gray-500" id="email-description">
      Please add all of the countries that you hold citizenship with
    </p>
    <p class="mt-2 text-sm text-red-600" v-if="nationalityError">{{ nationalityError }}</p>

    <div class="flex flex-wrap items-baseline gap-x-3 gap-y-2 pt-4">
      <template v-for="country in selectedCountries" :key="country.id">
        <div class="relative flex" @click="removeCountryById(country.id)">
          <StatusChip status="pledged" class="flex items-center gap-x-1"
            >{{ country.name }} <XMarkIcon class="size-3 text-zinc-400"
          /></StatusChip>
        </div>
      </template>
    </div>

    <div class="py-8">
      <div class="flex">
        <label for="ssn" class="block text-sm font-medium leading-6 text-gray-900"
          >Social Security Number</label
        >
        <QuestionMarkCircleIcon @click="showIntercomArticle" class="ml-1.5 w-4 text-gray-400" />
      </div>
      <div class="mt-2">
        <div class="relative mt-2 rounded-md shadow-sm">
          <input
            :type="showingTaxId ? 'text' : 'password'"
            autocomplete="off"
            v-model="taxId"
            inputmode="numeric"
            maxlength="9"
            ref="ssn"
            name="ssn"
            id="ssn"
            class="block w-full rounded-md border-0 py-1.5 pr-10 text-gray-900 ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-ig-blue sm:text-sm sm:leading-6"
            placeholder="000-00-0000"
          />
          <div class="absolute inset-y-0 right-0 flex items-center pr-3" @click="toggleTaxId">
            <EyeIcon v-if="!showingTaxId" class="h-5 w-5 text-gray-400" aria-hidden="true" />
            <EyeSlashIcon v-else class="h-5 w-5 text-gray-400" aria-hidden="true" />
          </div>
        </div>
      </div>
      <p class="mt-2 text-sm text-red-600" v-if="ssnError">{{ ssnError }}</p>
    </div>

    <div class="mb-12 rounded-lg bg-gray-50">
      <div class="flex items-center px-4 py-5 sm:p-6">
        <LockClosedIcon class="w-7 text-gray-400" />
        <div class="max-w-xl pl-4 text-sm text-gray-500">
          <p>We use bank-level encryption to protect your information.</p>
        </div>
      </div>
    </div>

    <button
      @click="save"
      type="button"
      :disabled="saving"
      class="w-full rounded-md bg-ig-blue px-3.5 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ig-blue"
    >
      Continue
    </button>
  </div>
</template>

<script setup>
import ProgressHeader from '@/components/ProgressHeader.vue'
import { computed, ref, defineEmits, defineProps, onMounted } from 'vue'
import { showArticle } from '@intercom/messenger-js-sdk'
import { API } from '@/API'
import {
  LockClosedIcon,
  QuestionMarkCircleIcon,
  EyeIcon,
  EyeSlashIcon,
} from '@heroicons/vue/20/solid'
import countries from '../assets/countries.json'
import { StatusChip, ComboBox, ComboBoxOption } from '@infinitegiving/vue-component-library'
import { XMarkIcon } from '@heroicons/vue/16/solid'

const emit = defineEmits(['complete'])
const props = defineProps(['user'])
const query = ref('')
const ssn = ref(null)
const citizenshipCountries = ref(null)
const taxId = ref(props.user.tax_id)
const showingTaxId = ref(false)
const saving = ref(false)
const ssnError = ref(null)
const nationalityError = ref(null)

const selectedCountries = ref([])
const filteredCountries = computed(() =>
  query.value === ''
    ? countries
    : countries.filter((country) => {
        return country.name.toLowerCase().includes(query.value.toLowerCase())
      })
)

const updateQueryString = (newQuery) => {
  query.value = newQuery
}

const showIntercomArticle = () => {
  showArticle(6630016)
}

const validate = () => {
  let saves = true
  ssnError.value = null
  nationalityError.value = null

  const taxIdEmpty = taxId.value == null || taxId.value == ''
  const taxIdInvalid = !/^\d{9}$/.test(taxId.value)
  const taxIdChanged = props.user.tax_id != taxId.value

  // If the tax ID is empty fail validation and show an error
  // If there is a value and it is the same as the value passed in, we already have that users SSN
  // and they haven't made changes so pass validation
  // If there is a value and it is different from the value passed in, check if it is a valid SSN and pass/fail based on that
  if (taxIdEmpty || (taxIdChanged && taxIdInvalid)) {
    ssnError.value = 'Please enter a valid SSN'
    saves = false
  }

  if (selectedCountries.value.length < 1) {
    nationalityError.value = 'Please select your citizenship countries'
    saves = false
  }

  return saves
}

const toggleTaxId = () => {
  showingTaxId.value = !showingTaxId.value
}

const save = async () => {
  if (!validate()) return

  const selectedNationalities = selectedCountries.value.map(({ id }) => {
    return id
  })

  try {
    saving.value = true
    const toSave = {
      tax_id: props.user.tax_id != taxId.value ? taxId.value : undefined,
      tax_id_type: 'ssn',
      nationalities: areNationalityArraysTheSame(
        props.user.nationalities || [],
        selectedNationalities
      )
        ? undefined
        : selectedNationalities,
    }

    if (toSave.tax_id || toSave.nationalities) await new API().save(toSave)
    await emit('complete')
  } catch (e) {
    alert('Could not save. Please check your input and try again.')
  } finally {
    saving.value = false
  }
}

const areNationalityArraysTheSame = (arr1, arr2) => {
  if (arr1.length !== arr2.length) {
    return false
  }
  const sortedArr1 = [...arr1].sort()
  const sortedArr2 = [...arr2].sort()

  for (let i = 0; i < sortedArr1.length; i++) {
    if (sortedArr1[i] !== sortedArr2[i]) {
      return false
    }
  }
  return true
}

const removeCountryById = (id) => {
  selectedCountries.value = selectedCountries.value.filter((country) => country.id !== id)
}

onMounted(() => {
  citizenshipCountries.value.focus()
})
</script>
